/**
 * /pressure-testing/instruments/:id — one instrument: its specs, its
 * calibration history, and the two writes on it (fieldiq-api#907
 * `GET /instruments/:id`, `PATCH /instruments/:id/specs`,
 * `POST /instruments/:id/calibrations`).
 *
 * The status, the due date and the days-until-due are the API's. Recording a
 * calibration invalidates every ['pressureTesting'] key, so this screen, the
 * register and the alerts re-read the API's new answer — an overdue gauge reads
 * Valid without a reload because the server says so, not because the screen
 * guessed it would.
 */
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CalendarCheck, Clock, Gauge, Loader2, Pencil, Plus } from 'lucide-react';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { usePtInstrument } from '../../hooks/usePressureTesting';
import { formatCalendarDate } from '../../lib/calendar-date';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import {
  canAdminPt,
  canCalibratePt,
  formatPressure,
  ptKindLabel,
  ptStatusSkin,
} from '../../lib/pressure-testing';
import { dueHint } from './InstrumentsScreen';
import { EditSpecsDialog, RecordCalibrationDialog } from './PtDialogs';
import { PT_SECONDARY, PtEmpty, PtPanel, PtSpec, PtStatusBadge } from './PtBits';

const day = (value: string | null | undefined) => formatCalendarDate(value) ?? '—';

export default function InstrumentDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const query = usePtInstrument(id);
  const [editing, setEditing] = useState(false);
  const [recording, setRecording] = useState(false);

  const instrument = query.data?.data ?? null;
  const meta = query.data?.meta;
  const unit = meta?.pressure_unit ?? null;
  const failure = queryLoadFailure(query);
  // A 404 is an answer about the record, not a failure to reach the server.
  const notFound = (queryFailureError(query) as { response?: { status?: number } } | null)?.response?.status === 404;
  const canCalibrate = canCalibratePt(user?.role) && !!instrument && !!meta;
  const canEdit = canAdminPt(user?.role) && !!instrument && !!unit;
  const status = instrument?.calibration_status;
  const calibrations = instrument?.calibrations ?? [];

  return (
    <div data-testid="pt-instrument-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Pressure testing · Instrument"
        title={instrument?.name || (query.isLoading ? 'Instrument' : '—')}
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          canCalibrate ? (
            <button type="button" onClick={() => setRecording(true)} className="fi-action fi-action--standard fi-action--primary">
              <Plus className="size-4" aria-hidden="true" />
              Record calibration
            </button>
          ) : undefined
        }
        stats={[
          {
            icon: <Gauge className="size-4" aria-hidden="true" />,
            label: 'Status',
            sub: 'Calibration',
            tone: status === 'valid' ? 'success' : status === 'overdue' || status === 'due_soon' ? 'accent' : 'neutral',
            loading: query.isLoading,
            value: instrument ? ptStatusSkin(status).label : '—',
          },
          {
            icon: <Clock className="size-4" aria-hidden="true" />,
            label: 'Due',
            sub: dueHint(instrument?.days_until_due) ?? undefined,
            tone: 'neutral',
            loading: query.isLoading,
            value: day(instrument?.latest_calibration?.due_on),
          },
          {
            icon: <CalendarCheck className="size-4" aria-hidden="true" />,
            label: 'Last cal',
            tone: 'neutral',
            loading: query.isLoading,
            value: day(instrument?.latest_calibration?.calibrated_on),
          },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {query.isLoading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading instrument" />
            </div>
          ) : notFound ? (
            <PtPanel eyebrow="Instrument" title="Instrument">
              <PtEmpty>This instrument was not found. It may have been archived or deleted.</PtEmpty>
            </PtPanel>
          ) : failure ? (
            <PtPanel eyebrow="Instrument" title="Instrument">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="this instrument" />
            </PtPanel>
          ) : !instrument ? (
            <PtPanel eyebrow="Instrument" title="Instrument">
              <PtEmpty>This instrument was not found.</PtEmpty>
            </PtPanel>
          ) : (
            <>
              <PtPanel
                eyebrow="Instrument"
                title="Specs"
                testId="pt-specs-panel"
                actions={
                  canEdit ? (
                    <button type="button" className={PT_SECONDARY} onClick={() => setEditing(true)}>
                      <Pencil className="size-4" aria-hidden="true" />
                      Edit specs
                    </button>
                  ) : undefined
                }
              >
                <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
                  <PtSpec label="Kind" value={ptKindLabel(instrument.specs?.instrument_kind)} />
                  <PtSpec label="Serial number" value={instrument.serial_number || '—'} mono />
                  <PtSpec label="Manufacturer" value={instrument.manufacturer || '—'} />
                  <PtSpec label="Model" value={instrument.model || '—'} />
                  <PtSpec label="Range min" value={formatPressure(instrument.specs?.range_min, unit)} mono />
                  <PtSpec label="Range max" value={formatPressure(instrument.specs?.range_max, unit)} mono />
                  <PtSpec label="Accuracy class" value={instrument.specs?.accuracy_class || '—'} />
                  {instrument.specs?.instrument_kind === 'recorder' && (
                    <PtSpec
                      label="Channels"
                      value={instrument.specs.recorder_channels == null ? '—' : String(instrument.specs.recorder_channels)}
                      mono
                    />
                  )}
                  <PtSpec label="Calibration status" value={<PtStatusBadge status={status} />} />
                </dl>
              </PtPanel>

              <PtPanel
                eyebrow="Append-only record"
                title="Calibration history"
                testId="pt-calibration-history"
                footer={calibrations.length > 0 ? `${calibrations.length} calibration${calibrations.length === 1 ? '' : 's'}, newest first` : undefined}
              >
                {calibrations.length === 0 ? (
                  <PtEmpty>No calibration has been recorded for this instrument.</PtEmpty>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[760px] text-left">
                      <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                        <tr>
                          <th scope="col" className="px-4 py-3">Calibrated</th>
                          <th scope="col" className="px-4 py-3">Due</th>
                          <th scope="col" className="px-4 py-3">Certificate #</th>
                          <th scope="col" className="px-4 py-3">Performed by</th>
                          <th scope="col" className="px-4 py-3">Notes</th>
                          <th scope="col" className="px-4 py-3">Entered by</th>
                        </tr>
                      </thead>
                      <tbody>
                        {calibrations.map((c) => (
                          <tr key={c.id} className="border-t border-fieldiq-border">
                            <td className="whitespace-nowrap px-4 py-3 text-sm text-fieldiq-text">{day(c.calibrated_on)}</td>
                            <td className="whitespace-nowrap px-4 py-3 text-sm text-fieldiq-text">{day(c.due_on)}</td>
                            <td className="px-4 py-3 font-mono text-sm text-fieldiq-text">{c.certificate_number || '—'}</td>
                            <td className="px-4 py-3 text-sm text-fieldiq-text">{c.performed_by || '—'}</td>
                            <td className="max-w-[24rem] px-4 py-3 text-sm text-fieldiq-text">{c.notes || '—'}</td>
                            <td className="px-4 py-3 text-sm text-fieldiq-muted">{c.created_by_name || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </PtPanel>
            </>
          )}
        </div>
      </section>

      {instrument && canEdit && (
        <EditSpecsDialog open={editing} onClose={() => setEditing(false)} instrument={instrument} unit={unit} />
      )}
      {instrument && canCalibrate && (
        <RecordCalibrationDialog
          open={recording}
          onClose={() => setRecording(false)}
          instrumentId={instrument.id}
          today={meta?.today ?? null}
        />
      )}
    </div>
  );
}
